import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  BAND_SIZE,
  filledRect,
  makeRound,
  memberSpot,
  portraitAsset,
  riserSlotCenter,
  shuffledStages,
  spriteCenter,
  stageImage,
} from "../components/games/animal-sound-farm/gameUtils";
import StageAnimal from "../components/games/animal-sound-farm/FarmAnimal";
import BandMember from "../components/games/animal-sound-farm/AnimalCard";
import { useFarmSoundManager } from "../components/games/animal-sound-farm/SoundManager";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import BackToGamesButton from "../components/games/BackToGamesButton";
import { SpeakerIcon } from "../components/games/ui-icons";
import "./AnimalSoundFarmPage.css";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const CONFETTI_COLORS = ["#ff6b6b", "#ffd43b", "#52cf66", "#4dabf7", "#b097fc"];
const CONFETTI_BITS = Array.from({ length: 18 }, (_, index) => ({
  id: index,
  left: `${7 + ((index * 29) % 87)}%`,
  delay: `${(index % 6) * 0.08}s`,
  color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
}));

const bubbleText = (animal) => {
  const label = animal.soundLabel;
  if (!label) return "!";
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}!`;
};

// MARK: - Ambience particles (pollen by day, fireflies at night, snow in winter)

const AMBIENCE_COUNT = { pollen: 12, fireflies: 9, snow: 16 };

const SceneAmbience = ({ kind }) => {
  const particles = useMemo(() => {
    const count = AMBIENCE_COUNT[kind] || 10;
    return Array.from({ length: count }, (_, index) => ({
      id: index,
      left: `${(index * 61.8) % 100}%`,
      top: `${(index * 37.5) % 90}%`,
      dur: `${3.4 + ((index * 7) % 5) * 0.6}s`,
      delay: `${(index % 6) * 0.5}s`,
      size: kind === "snow" ? 3 + (index % 3) : kind === "fireflies" ? 5 + (index % 3) : 2 + (index % 2),
    }));
  }, [kind]);

  return (
    <div className={`asf-ambience asf-ambience-${kind}`} aria-hidden="true">
      {particles.map((p) => (
        <span
          key={p.id}
          style={{
            left: p.left,
            top: p.top,
            width: `${p.size}px`,
            height: `${p.size}px`,
            "--dur": p.dur,
            "--delay": p.delay,
          }}
        />
      ))}
    </div>
  );
};

// MARK: - Eight little stars shooting outward when a friend is recruited

const SparkleBurst = () => (
  <div className="asf-sparkle-burst" aria-hidden="true">
    {Array.from({ length: 8 }).map((_, index) => {
      const angle = (index / 8) * 2 * Math.PI;
      return (
        <span
          key={index}
          style={{
            "--tx": `${Math.cos(angle) * 84}px`,
            "--ty": `${Math.sin(angle) * 84}px`,
            fontSize: index % 2 === 0 ? "15px" : "10px",
          }}
        >
          ✦
        </span>
      );
    })}
  </div>
);

// MARK: - The showtime celebration card

const ShowtimeCard = ({ stageName, friends, starsAwarded, shows }) => (
  <motion.div
    className="asf-showtime-card"
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    exit={{ scale: 0.8, opacity: 0 }}
    transition={{ type: "spring", stiffness: 220, damping: 18 }}
  >
    <h3>Showtime!</h3>
    <div className="asf-showtime-portraits">
      {friends.map((friend, index) => (
        <motion.img
          key={friend.id}
          src={portraitAsset(friend.id)}
          alt={friend.name}
          draggable="false"
          initial={{ scale: 0.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.25 + index * 0.18 }}
        />
      ))}
    </div>
    <p>The {stageName} band performs!</p>
    <div className="asf-showtime-tractor" aria-hidden="true">
      <img src="/assets/games/animal-sound-farm/asf_tractor.png" alt="" draggable="false" />
    </div>
    {starsAwarded > 0 && shows === 0 && (
      <div className="asf-star-badge">★ +{starsAwarded} stars earned!</div>
    )}
  </motion.div>
);

// MARK: - Intro overlay

const IntroOverlay = ({ onStart }) => (
  <div className="asf-intro-backdrop" role="dialog" aria-modal="true" aria-labelledby="asf-intro-title">
    <div className="asf-intro-card">
      <div className="asf-intro-hero">
        <img src="/assets/games/animal-sound-farm/asf_intro_hero.png" alt="" draggable="false" />
      </div>
      <div className="asf-intro-copy">
        <h2 id="asf-intro-title">Start the Barnyard Band!</h2>
        <p>The animals are putting on a farm show — and they need singers. Can you find them by their voices?</p>
      </div>
      <div className="asf-intro-steps">
        <div className="asf-intro-step"><span>1</span><span>Listen to the mystery singer&apos;s voice.</span></div>
        <div className="asf-intro-step"><span>2</span><span>Tap animals to hear them — find that voice!</span></div>
        <div className="asf-intro-step"><span>3</span><span>Recruit three singers, then it&apos;s showtime!</span></div>
      </div>
      <button type="button" className="asf-intro-button" onClick={onStart}>♪ Start the show!</button>
    </div>
  </div>
);

// MARK: - Page

const AnimalSoundFarmPage = () => {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { refreshUserData } = useAuth();
  const sounds = useFarmSoundManager();

  const reduceMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  // One-time initial casting so state and refs start in sync.
  const initial = useRef(null);
  if (!initial.current) {
    const deck = shuffledStages();
    const round = makeRound({ stage: deck[0], bandCount: 0, showsCompleted: 0, excluding: [], previousTargetId: null });
    initial.current = { deck, round };
  }

  const [stageDeck, setStageDeck] = useState(initial.current.deck);
  const [stageIndex, setStageIndex] = useState(0);
  const [cast, setCast] = useState(initial.current.round.cast);
  const [target, setTarget] = useState(initial.current.round.target);
  const [band, setBand] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const [showsCompleted, setShowsCompleted] = useState(0);
  const [starsAwarded, setStarsAwarded] = useState(0);

  const [started, setStarted] = useState(false);
  const [showtime, setShowtime] = useState(false);
  const [feedback, setFeedback] = useState({ kind: "neutral", text: "Tap the sound button to hear the mystery singer." });
  const [reaction, setReaction] = useState(null); // { id, tick }
  const [flight, setFlight] = useState(null);
  const [landedCount, setLandedCount] = useState(0);
  const [missStreak, setMissStreak] = useState(0);

  const [content, setContent] = useState(null);
  const [landscape, setLandscape] = useState(false);
  const stageRef = useRef(null);

  // Refs mirror state values read inside async flows.
  const genRef = useRef(0);
  const lockedRef = useRef(false);
  const startedRef = useRef(false);
  const showtimeRef = useRef(false);
  const targetRef = useRef(initial.current.round.target);
  const bandRef = useRef([]);
  const castRef = useRef(initial.current.round.cast);
  const stageDeckRef = useRef(initial.current.deck);
  const stageIndexRef = useRef(0);
  const showsRef = useRef(0);
  const missRef = useRef(0);
  const totalScoreRef = useRef(0);
  const prevTargetRef = useRef(initial.current.round.target.id);
  const reactionTickRef = useRef(0);
  const contentRef = useRef(null);

  const stage = stageDeck[stageIndex];

  // Keep a live reference to the (frequently re-created) sound manager so the
  // unmount cleanup can reach it without re-running every audio beat.
  const soundsRef = useRef(sounds);
  useEffect(() => { soundsRef.current = sounds; }, [sounds]);

  // MARK: Measure the stage; pick portrait / wide art by container aspect.
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      const isLandscape = w > h;
      const rect = filledRect(w, h, isLandscape);
      setLandscape(isLandscape);
      setContent(rect);
      contentRef.current = rect;
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => () => {
    genRef.current += 1;
    soundsRef.current.stop();
  }, []);

  const setFeedbackSafe = useCallback((kind, text) => setFeedback({ kind, text }), []);

  const persistProgress = useCallback(
    async (score) => {
      if (!apiClient || score < BAND_SIZE) return;
      try {
        const response = await apiClient.postGameProgress("ANIMAL_SOUND_FARM", score, true, null);
        if (response) {
          if (typeof response.stars_awarded === "number" && response.stars_awarded > 0) {
            setStarsAwarded(response.stars_awarded);
          } else if (typeof response.starsAwarded === "number" && response.starsAwarded > 0) {
            setStarsAwarded(response.starsAwarded);
          }
          window.dispatchEvent(new CustomEvent("games:progress-updated"));
          refreshUserData?.();
        }
      } catch {
        // Saving progress must never interrupt play.
      }
    },
    [apiClient, refreshUserData]
  );

  const buildRound = useCallback(() => {
    const currentStage = stageDeckRef.current[stageIndexRef.current];
    const round = makeRound({
      stage: currentStage,
      bandCount: bandRef.current.length,
      showsCompleted: showsRef.current,
      excluding: bandRef.current,
      previousTargetId: prevTargetRef.current,
    });
    castRef.current = round.cast;
    targetRef.current = round.target;
    prevTargetRef.current = round.target.id;
    setCast(round.cast);
    setTarget(round.target);
  }, []);

  const playMysterySoon = useCallback(
    (delay) => {
      const gen = genRef.current;
      window.setTimeout(() => {
        if (gen !== genRef.current) return;
        if (startedRef.current && !showtimeRef.current && !lockedRef.current && targetRef.current) {
          sounds.playMystery(targetRef.current);
        }
      }, delay * 1000);
    },
    [sounds]
  );

  const playTargetSound = useCallback(() => {
    if (!startedRef.current || showtimeRef.current || lockedRef.current || !targetRef.current) return;
    sounds.playMystery(targetRef.current);
    setFeedbackSafe("neutral", "Which friend has that voice?");
  }, [setFeedbackSafe, sounds]);

  const runCorrect = useCallback(
    async (member, spot, contentSnapshot) => {
      const gen = genRef.current;
      const slotIndex = bandRef.current.length;

      missRef.current = 0;
      setMissStreak(0);
      setFeedbackSafe("correct", `The ${member.animal.name.toLowerCase()} joins the band!`);
      sounds.playCorrect();

      // Recruit: the found singer leaves the field for the riser.
      castRef.current = castRef.current.filter((m) => m.animal.id !== member.animal.id);
      setCast(castRef.current);
      const newBand = [...bandRef.current, member.animal];
      bandRef.current = newBand;
      setBand(newBand);
      totalScoreRef.current += 1;
      setTotalScore(totalScoreRef.current);

      // Flight from the spot to the riser slot.
      const popCenter = spriteCenter(spot, contentSnapshot);
      const startHeight = contentSnapshot.height * spot.scale;
      const popHeight = Math.max(contentSnapshot.height * spot.scale * 1.25, contentSnapshot.height * 0.24);
      const clampedX = Math.min(Math.max(popCenter.x, popHeight * 0.5), contentSnapshot.width - popHeight * 0.5 + contentSnapshot.minX * 2);
      const clampedY = Math.min(Math.max(popCenter.y - 8, popHeight * 0.55), contentSnapshot.minY + contentSnapshot.height - popHeight * 0.5);

      setFlight({
        animal: member.animal,
        center: { x: clampedX, y: clampedY },
        height: startHeight,
        bubble: false,
        transition: { type: "spring", stiffness: 260, damping: 18 },
      });
      await sleep(30);
      if (gen !== genRef.current) return;
      setFlight((prev) => (prev ? { ...prev, height: popHeight } : prev));
      await sleep(200);
      if (gen !== genRef.current) return;
      setFlight((prev) => (prev ? { ...prev, bubble: true } : prev));

      await sleep(1150);
      if (gen !== genRef.current) return;
      const riserCenter = riserSlotCenter(slotIndex, contentSnapshot);
      setFlight((prev) =>
        prev
          ? {
              ...prev,
              center: riserCenter,
              height: contentSnapshot.height * 0.185,
              bubble: false,
              transition: { duration: 0.5, ease: "easeInOut" },
            }
          : prev
      );
      await sleep(520);
      if (gen !== genRef.current) return;
      setFlight(null);
      setLandedCount((count) => count + 1);

      // The new member debuts: the groove restarts with their voice.
      sounds.setBand(newBand);

      if (newBand.length >= BAND_SIZE) {
        persistProgress(totalScoreRef.current);
        await sleep(1500);
        if (gen !== genRef.current) return;
        sounds.playCelebration();
        sounds.setBand(newBand, true);
        showtimeRef.current = true;
        setShowtime(true);
        setFeedbackSafe("correct", "The band is ready — on with the show!");

        await sleep(5400);
        if (gen !== genRef.current) return;
        sounds.setBand([]);
        // Advance to the next stage with a fresh band.
        showsRef.current += 1;
        setShowsCompleted(showsRef.current);
        bandRef.current = [];
        setBand([]);
        let nextIndex = stageIndexRef.current + 1;
        if (nextIndex >= stageDeckRef.current.length) {
          const freshDeck = shuffledStages(stageDeckRef.current[stageIndexRef.current].id);
          stageDeckRef.current = freshDeck;
          setStageDeck(freshDeck);
          nextIndex = 0;
        }
        stageIndexRef.current = nextIndex;
        setStageIndex(nextIndex);
        buildRound();
        setShowtime(false);
        showtimeRef.current = false;
        setLandedCount(0);
        lockedRef.current = false;
        setFeedbackSafe("neutral", "A new stage! Listen for your first singer.");
        playMysterySoon(0.7);
      } else {
        await sleep(1900);
        if (gen !== genRef.current) return;
        buildRound();
        lockedRef.current = false;
        setFeedbackSafe("neutral", "Great ears! Who sings next?");
        playMysterySoon(0.5);
      }
    },
    [buildRound, persistProgress, playMysterySoon, setFeedbackSafe, sounds]
  );

  const handleTap = useCallback(
    (member, spot) => {
      if (!startedRef.current || showtimeRef.current || lockedRef.current) return;

      // Every animal answers with its own voice — the toybox promise.
      sounds.playTapped(member.animal);
      const tick = reactionTickRef.current + 1;
      reactionTickRef.current = tick;
      setReaction({ id: member.animal.id, tick });
      const gen = genRef.current;
      window.setTimeout(() => {
        if (gen !== genRef.current || reactionTickRef.current !== tick) return;
        setReaction(null);
      }, 1500);

      if (member.animal.id !== targetRef.current?.id) {
        const newMiss = missRef.current + 1;
        missRef.current = newMiss;
        setMissStreak(newMiss);
        if (newMiss >= 2) {
          setFeedbackSafe("wrong", `That's the ${member.animal.name.toLowerCase()}! Follow the glow to the singer.`);
        } else {
          setFeedbackSafe("wrong", `That's the ${member.animal.name.toLowerCase()}! But who was singing?`);
        }
        playMysterySoon(1.6);
        return;
      }

      // Found the singer!
      lockedRef.current = true;
      const snapshot = contentRef.current;
      if (snapshot) runCorrect(member, spot, snapshot);
    },
    [playMysterySoon, runCorrect, setFeedbackSafe, sounds]
  );

  const handleStart = useCallback(() => {
    sounds.prewarm();
    startedRef.current = true;
    setStarted(true);
    setFeedbackSafe("neutral", "Which friend has that voice?");
    playMysterySoon(0.5);
  }, [playMysterySoon, setFeedbackSafe, sounds]);

  const handleExit = useCallback(() => {
    genRef.current += 1;
    sounds.stop();
    persistProgress(totalScoreRef.current);
    navigate("/games");
  }, [navigate, persistProgress, sounds]);

  // MARK: Derived render data

  const sortedCast = useMemo(() => {
    if (!content) return [];
    return [...cast]
      .map((member) => ({ member, spot: memberSpot(member, stage, landscape) }))
      .sort((a, b) => a.spot.y - b.spot.y);
  }, [cast, content, landscape, stage]);

  const singerSpot = useMemo(() => {
    if (!content || !target) return null;
    const member = cast.find((m) => m.animal.id === target.id);
    if (!member) return null;
    return memberSpot(member, stage, landscape);
  }, [cast, content, landscape, stage, target]);

  const isPlaying = sounds.isPlayingAnimal;
  const showGlow = missStreak >= 2 && !showtime && !flight && singerSpot;
  const showHelper = missStreak >= 3 && !showtime && target;

  return (
    <main className="animal-sound-farm">
      <div className="farm-game-shell">
        <header className="farm-topbar">
          <BackToGamesButton onClick={handleExit} accent="#26703c" />
          <div className="farm-title-wrap">
            <h1>Animal Sound Farm</h1>
            <p>Listen • Look • Tap</p>
          </div>
          <button
            type="button"
            className="farm-icon-button"
            onClick={sounds.toggleMute}
            aria-label={sounds.muted ? "Turn sound on" : "Mute sounds"}
            aria-pressed={sounds.muted}
          >
            <span className="farm-icon-glyph" aria-hidden="true"><SpeakerIcon size={22} muted={sounds.muted} /></span>
          </button>
        </header>

        <section className="asf-stage-section" aria-label="Barnyard Band stage">
          <div className={`asf-stage asf-ambience-host ${started ? "is-live" : ""}`} ref={stageRef}>
            <img
              className="asf-stage-bg"
              src={stageImage(stage, landscape)}
              alt={`${stage.name}. Listen, then tap the animal whose voice it was.`}
              draggable="false"
            />

            {!reduceMotion && <SceneAmbience key={`amb-${stage.id}`} kind={stage.ambience} />}

            {content && (
              <>
                {/* Hint halo under the singer once the child has missed twice. */}
                {showGlow && (
                  <div
                    className="asf-hint-glow"
                    aria-hidden="true"
                    style={(() => {
                      const c = spriteCenter(singerSpot, content);
                      const size = content.height * singerSpot.scale * 1.6;
                      return { left: `${c.x}px`, top: `${c.y}px`, width: `${size}px`, height: `${size}px` };
                    })()}
                  />
                )}

                {/* The live cast, farthest (highest, smallest) first. */}
                {sortedCast.map(({ member, spot }) => (
                  <StageAnimal
                    key={member.animal.id}
                    member={member}
                    spot={spot}
                    content={content}
                    reactionTick={reaction?.id === member.animal.id ? reaction.tick : 0}
                    wantsAttention={missStreak >= 3 && member.animal.id === target?.id && !reduceMotion}
                    reduceMotion={reduceMotion}
                    onTap={() => handleTap(member, spot)}
                  />
                ))}

                {/* Showtime dims the field and sweeps spotlights. */}
                {showtime && (
                  <>
                    <div className={`asf-dim asf-dim-${stage.ambience}`} aria-hidden="true" />
                    {!reduceMotion && (
                      <div className="asf-spotlights" aria-hidden="true">
                        <span className="asf-beam asf-beam-left" />
                        <span className="asf-beam asf-beam-right" />
                      </div>
                    )}
                    <div className="asf-confetti" aria-hidden="true">
                      {CONFETTI_BITS.map((bit) => (
                        <i key={bit.id} style={{ left: bit.left, "--delay": bit.delay, "--color": bit.color }} />
                      ))}
                    </div>
                  </>
                )}

                {/* Riser: the band's front stage. */}
                <div className="asf-riser-layer" aria-hidden="true">
                  <div
                    className="asf-riser-platform"
                    style={{
                      // Platform tracks the riser slots: centred on landscape,
                      // packed left on portrait (see riserSlotCenter).
                      left: `${content.minX + content.width * (content.width > content.height ? 0.5 : 0.38)}px`,
                      top: `${content.minY + content.height * 0.9405}px`,
                      width: `${content.width * (content.width > content.height ? 0.68 : 0.58)}px`,
                      height: `${content.height * 0.075}px`,
                    }}
                  />
                  {Array.from({ length: BAND_SIZE }).map((_, index) => {
                    const slot = riserSlotCenter(index, content);
                    const showMember = index < Math.min(landedCount, band.length);
                    if (showMember) {
                      return (
                        <div
                          key={`slot-${index}`}
                          className="asf-riser-slot"
                          style={{ left: `${slot.x}px`, top: `${slot.y}px` }}
                        >
                          <BandMember
                            animal={band[index]}
                            height={content.height * 0.185}
                            singing={sounds.singingID === band[index].id}
                            reduceMotion={reduceMotion}
                          />
                        </div>
                      );
                    }
                    return (
                      <div
                        key={`slot-${index}`}
                        className="asf-riser-mark"
                        style={{
                          left: `${slot.x}px`,
                          top: `${content.minY + content.height * 0.945}px`,
                          width: `${content.height * 0.11}px`,
                          height: `${content.height * 0.038}px`,
                        }}
                      />
                    );
                  })}
                </div>

                {/* Flight: recruit flying to the riser. */}
                <AnimatePresence>
                  {flight && (
                    <motion.div
                      className="asf-flight"
                      initial={false}
                      animate={{ x: flight.center.x, y: flight.center.y }}
                      transition={flight.transition}
                    >
                      <div className="asf-flight-inner">
                        <SparkleBurst />
                        <motion.img
                          className="asf-flight-sprite"
                          src={`/assets/games/animal-sound-farm/asf_cutout_${flight.animal.id}.png`}
                          alt=""
                          draggable="false"
                          initial={false}
                          animate={{ height: flight.height }}
                          transition={flight.transition}
                        />
                        <AnimatePresence>
                          {flight.bubble && (
                            <motion.div
                              className="asf-flight-bubble"
                              initial={{ scale: 0.1, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.1, opacity: 0 }}
                              transition={{ type: "spring", stiffness: 300, damping: 16 }}
                            >
                              {bubbleText(flight.animal)} That&apos;s me!
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Showtime card. */}
                <AnimatePresence>
                  {showtime && (
                    <div className="asf-showtime-layer">
                      <ShowtimeCard
                        stageName={stage.name}
                        friends={band}
                        starsAwarded={starsAwarded}
                        shows={showsCompleted}
                      />
                    </div>
                  )}
                </AnimatePresence>
              </>
            )}

            {/* HUD: star progress top, control dock bottom. */}
            <div className="asf-progress-pill" aria-label={`${band.length} of ${BAND_SIZE} singers found`}>
              {Array.from({ length: BAND_SIZE }).map((_, index) => (
                <span key={index} className={index < band.length ? "filled" : ""}>
                  {index < band.length ? "★" : "☆"}
                </span>
              ))}
            </div>

            {!showtime && (
              <>
                {/* Informational stack lives in the SKY (under the star pill)
                    where no animal ever stands — the corner stack was tall
                    enough with two-line messages to hide the right-side
                    singers, and it swallowed their clicks. */}
                <div className="asf-info" aria-hidden={!showHelper && !feedback.text}>
                  <div className={`asf-feedback asf-feedback-${feedback.kind}`} aria-live="polite">
                    {feedback.text}
                  </div>
                  {showHelper && (
                    <div className="asf-helper" aria-hidden="true">
                      <img src={`/assets/games/animal-sound-farm/asf_cutout_${target.id}.png`} alt="" draggable="false" />
                      <span>Find me!</span>
                    </div>
                  )}
                </div>
                <div className="asf-dock">
                  <button
                    type="button"
                    className={`asf-speaker ${isPlaying ? "is-playing" : ""}`}
                    onClick={playTargetSound}
                    disabled={!started || showtime || sounds.muted}
                    aria-label="Hear the mystery singer again"
                  >
                    {!isPlaying && !sounds.muted && <span className="asf-speaker-invite" aria-hidden="true" />}
                    {isPlaying && <span className="asf-speaker-ripple" aria-hidden="true" />}
                    <span className="asf-speaker-glyph" aria-hidden="true"><SpeakerIcon size={30} /></span>
                  </button>
                </div>
              </>
            )}
          </div>

          {!started && <IntroOverlay onStart={handleStart} />}
        </section>
      </div>
    </main>
  );
};

export default AnimalSoundFarmPage;
